// @flow

import * as React from 'react';
import { Client, createRequest, useQuery } from 'urql';
import { share, pipe, filter, merge, map, tap } from 'wonka';
import type {
  ExchangeInput,
  ExchangeIO,
  Operation,
  OperationResult,
  UseQueryResponse,
  TypedDocumentNode,
} from 'urql';

// Key for the urql operation context.
export const cacheInvalidationPredicatesKey = 'invalidationPredicates';

// Return types for InvalidationPredicates.
export type InvalidationPredicateResponse =
  | 'validate' // The query is still valid, don't invalidate
  | 'invalidateAndRefetch' // The query is no longer valid, and should be refetched
  | 'invalidateOnly'; // The query is no longer valid, but don't bother refetching -- just remove it from the cache

// Mutation field name to predicate.
export type InvalidationPredicates = { [string]: (any) => InvalidationPredicateResponse };

type UseInvalidatableQueryArgs<T, V> = {|
  query: string | TypedDocumentNode<T, V>,
  variables?: V,
  executeWhen?: 'immediately' | 'afterInvalidation' | 'never',
|};

type BasicQueryArgs<T, V> = {|
  query: string | TypedDocumentNode<T, V>,
  variables?: V,
|};

/**
 * Like urql.useQuery but allows the results of the query to be invalidated in the urql cache when mutations that
 * affect this query's data happen.
 *
 * @param args See useQuery.
 * @param invalidationPredicates A map of mutation field name to a predicate on the mutation's variables.  If the
 * predicate evaluates to true, this query is evicted from the cache and, if its results are still being rendered,
 * it is immediately executed again.
 */
export const useInvalidatableQuery = <T, V: {}>(
  args: UseInvalidatableQueryArgs<T, V>,
  invalidationPredicates: InvalidationPredicates,
): UseQueryResponse<T> => {
  const executeWhen = args.executeWhen || 'immediately';

  const context = React.useMemo(
    () => ({
      [cacheInvalidationPredicatesKey]: invalidationPredicates,
    }),
    // The best we can do with the predicates is check the keys.  There is no way to do function equality reliably
    // as any inline functions (the kind we expect clients to generally use) will have distinct reference identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...Object.keys(invalidationPredicates)],
  );

  return useQuery({
    variables: args.variables,
    query: args.query,
    requestPolicy: executeWhen === 'afterInvalidation' ? 'cache-only' : undefined,
    pause: executeWhen === 'never',
    context,
  });
};

/**
 * A simple wrapper around executeQuery to inject invalidationPredicates into the exchange.
 * If you want the state like you get with the useQuery hook, use useInvalidatableQuery
 */
export const executeInvalidatableQuery = <T, V: {}>(
  client: Client,
  args: BasicQueryArgs<T, V>,
  invalidationPredicates: InvalidationPredicates,
) =>
  client.executeQuery(createRequest(args.query, args.variables), {
    [cacheInvalidationPredicatesKey]: invalidationPredicates,
  });

const reexecuteOperation = (client: Client, operation: Operation) =>
  client.reexecuteOperation({
    ...operation,
    context: {
      ...operation.context,
      // By doing this we ensure the operation skips us when it comes back around.
      requestPolicy: 'network-only',
    },
  });

/**
 * An urql Exchange that caches denormalized responses, much like the builtin one.  However, its eviction strategy is
 * different, as outlined in the doc for {@link #useInvalidatableQuery}.
 *
 * TODO future enhancement: evict queries from the cache based on teardown operations.  Don't just evict on teardown
 * (that would be too aggressive as query components could come back), but rather expose a function that, when invoked,
 * evicts all torn-down operations from the cache.  We could invoke this function on page transitions.
 */
export class DenormalizedCacheExchange {
  resultCache: Map<number, OperationResult<any>>;
  invalidationPredicates: Map<string, Map<number, (any) => InvalidationPredicateResponse>>;

  constructor() {
    // The cache of query result data, indexed by query request key.
    this.resultCache = new Map<number, OperationResult<any>>();
    // Cache invalidation predicates, indexed by mutation field name and query request key.  After we mutate a field,
    // we execute all predicates against that mutation's request variables.  If the predicate holds true, we invalidate
    // the cache response for the given query request key and reexecute that query.
    //
    // At this time the predicates' arguments are untyped though there may be a way to map from the mutation field name
    // to the mutation's variables type, which would allow us to type the predicates.
    this.invalidationPredicates = new Map<
      string,
      Map<number, (any) => InvalidationPredicateResponse>,
    >();
  }

  // These getters are intended for consumption by the debug tool.
  get resultCache(): Map<number, OperationResult<any>> {
    return this.resultCache;
  }
  get invalidationPredicates(): Map<string, Map<number, (any) => InvalidationPredicateResponse>> {
    return this.invalidationPredicates;
  }

  exchange({ forward, client }: ExchangeInput): ExchangeIO {
    // Whether this operation is unable to be handled by this exchange and should be forwarded to the next one unchanged.
    const shouldSkip = ({ operationName }: Operation) =>
      operationName !== 'mutation' && operationName !== 'query';

    const isOperationCached = operation => {
      const {
        key,
        operationName,
        context: { requestPolicy },
      } = operation;
      return (
        operationName === 'query' &&
        requestPolicy !== 'network-only' &&
        (requestPolicy === 'cache-only' || this.resultCache.has(key))
      );
    };

    // Declaring this separately to work around https://github.com/prettier/prettier/issues/4607.
    type mutationPredicate = any => InvalidationPredicateResponse;

    const afterQuery = (response: OperationResult<any>) => {
      const {
        operation: { key },
        data,
      } = response;
      if (data === undefined) {
        return;
      }

      this.resultCache.set(key, response);
    };

    const addInvalidationPredicates = operation => {
      const { key } = operation;
      const predicates = operation.context[cacheInvalidationPredicatesKey];

      if (predicates) {
        Object.entries(predicates).forEach(([fieldName, predicate]) => {
          const byField = this.invalidationPredicates.get(fieldName) || new Map();
          byField.set(key, ((predicate: any): mutationPredicate));
          if (byField.size === 1) {
            this.invalidationPredicates.set(fieldName, byField);
          }
        });
      }
    };

    const afterMutation = (response: OperationResult<any>) => {
      // Invalidate any queries that are hooked onto this mutation's field name(s).
      const invalidatedQueries = new Map<number, InvalidationPredicateResponse>();
      const fieldNames: Array<string> = [];
      response.operation.query.definitions.forEach(d => {
        if (d.selectionSet && d.selectionSet.selections) {
          fieldNames.push(
            ...d.selectionSet.selections
              .filter(s => s.kind === 'Field')
              .map(field => field.name.value),
          );
        }
      });

      fieldNames.forEach(fieldName => {
        const predicates = this.invalidationPredicates.get(fieldName);
        if (predicates) {
          Array.from(predicates.entries())
            .map(([queryKey, predicate]) => [queryKey, predicate(response.operation.variables)])
            .filter(([, predicateResponse]) => predicateResponse !== 'validate')
            .forEach(
              ([queryKey, predicateResponse]) =>
                invalidatedQueries.set(queryKey, predicateResponse) && predicates.delete(queryKey),
            );

          if (predicates.size === 0) {
            this.invalidationPredicates.delete(fieldName);
          }
        }
      });

      invalidatedQueries.forEach((predicateResponse, key) => {
        if (this.resultCache.has(key)) {
          const cachedResult = this.resultCache.get(key);
          this.resultCache.delete(key);
          if (cachedResult && predicateResponse === 'invalidateAndRefetch') {
            reexecuteOperation(client, cachedResult.operation);
          }
        }
      });
    };

    return ops$ => {
      const sharedOps$ = share(ops$);

      // Operations that are in the cache
      const cachedOps$ = pipe(
        sharedOps$,
        filter(op => !shouldSkip(op) && isOperationCached(op)),
        tap(op => addInvalidationPredicates(op)),
        map(operation => {
          const {
            key,
            context: { requestPolicy },
          } = operation;
          const cachedResult = this.resultCache.get(key);
          if (requestPolicy === 'cache-and-network') {
            reexecuteOperation(client, operation);
          }

          if (cachedResult !== undefined) {
            return cachedResult;
          }

          const result = {
            operation,
            data: undefined,
            error: undefined,
          };

          this.resultCache.set(key, result);

          return result;
        }),
      );

      const forwardedOps$ = pipe(
        sharedOps$,
        filter(op => shouldSkip(op) || !isOperationCached(op)),
        tap(op => addInvalidationPredicates(op)),
        forward,
        tap(response => {
          if (response.operation && response.operation.operationName === 'mutation') {
            afterMutation(response);
          } else if (response.operation && response.operation.operationName === 'query') {
            afterQuery(response);
          }
        }),
      );

      return merge([cachedOps$, forwardedOps$]);
    };
  }
}
